import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import Linkify from 'linkify-react';
import DateUtils from 'lib/DateUtils';
import StringUtils from 'lib/StringUtils';
import { MESSAGE_STATUS, ERROR_MESSAGE } from 'utils/enum/core/Core';
import MessageStyles from 'styles/widgets/MessageStyles';
import PriorityHighIcon from '@material-ui/icons/PriorityHigh';
import {
    makeStyles, Avatar, Typography, Tooltip,
} from '@material-ui/core';
import CreatedOnWithAvatar from 'components/modules/crm/activities/CreatedOnWithAvatar';
import MMSMediaCard from 'components/widgets/sms/MMSMediaCard';
import { isFinite } from 'lodash';

const useStyle = makeStyles((theme) => MessageStyles.ShortMessageService(theme));
const getFailedMessage = (errorCode) => {
    const currentErrorCode = Number(errorCode);
    return isFinite(currentErrorCode) ? ERROR_MESSAGE[currentErrorCode] : '';
};

const failedTooltipProps = {
    placement: 'bottom',
    PopperProps: {
        modifiers: {
            flip: { enabled: false },
        },
    },
};

const ShortMessageService = ({ record, crmId }) => {
    const classes = useStyle();
    const isOutgoing = record.direction === 'Outgoing';
    const isFailed = record.status?.toUpperCase() === MESSAGE_STATUS.FAILED.toUpperCase();
    const hasMessage = !StringUtils.isEmpty(record.message);
    const avatar = isOutgoing ? <CreatedOnWithAvatar record={record} hiddenDate className={classes.avatarOutgoing} /> : <Avatar className={classes.avatar} />;

    return (
        <>
            <div className={clsx(classes.main, { [classes.boxMessageOut]: isOutgoing })}>
                {avatar}
                <div className={clsx(classes.message, classes[`message${record.direction}`])}>
                    {hasMessage && (
                        <Typography
                            className={clsx(classes.text, classes[`text${record.direction}`])}
                        >
                            <Linkify
                                options={{ target: '_blank' }}
                            >
                                {record.message}
                            </Linkify>
                            {isFailed && (
                                <Tooltip title={getFailedMessage(record.errorCode)} {...failedTooltipProps}>
                                    <span className={classes.iconFailAnchor}>
                                        <PriorityHighIcon fontSize="small" className={classes.iconFail} />
                                    </span>
                                </Tooltip>
                            )}
                        </Typography>
                    )}
                    {!hasMessage ? (
                        <div className={classes.mediaMessageRow}>
                            <div className={classes.mediaMessageContent}>
                                {record.attachments?.map((attachment) => (
                                    <MMSMediaCard
                                        key={attachment.messageAttachmentId}
                                        attachment={attachment}
                                        crmId={crmId}
                                    />
                                ))}
                            </div>
                            {isFailed && (
                                <Tooltip title={getFailedMessage(record.errorCode)} {...failedTooltipProps}>
                                    <span className={classes.iconFailWrapper}>
                                        <PriorityHighIcon fontSize="small" className={classes.iconFail} />
                                    </span>
                                </Tooltip>
                            )}
                        </div>
                    ) : (
                        record.attachments?.map((attachment) => (
                            <MMSMediaCard
                                key={attachment.messageAttachmentId}
                                attachment={attachment}
                                crmId={crmId}
                            />
                        ))
                    )}
                    <div className={classes.flexEnd}>
                        <Tooltip title={DateUtils.fromNow(record.createdOn)} placement="top">
                            <Typography
                                className={clsx(classes.dateTime, classes[`text${record.direction}`])}
                            >
                                {DateUtils.getFormattedDateInUserTimezone(record.createdOn)}
                            </Typography>
                        </Tooltip>
                    </div>
                </div>
            </div>
            {record.markNotes?.length > 0 && (
                <div className={classes.markNotes}>
                    {record.markNotes.map((note, index) => (
                        <div className={note?.includes('unread') ? 'unread' : 'read'} key={index}>{note}</div>
                    ))}
                </div>
            )}
        </>
    );
};

ShortMessageService.propTypes = {
    record: PropTypes.object.isRequired,
    crmId: PropTypes.string,
};

ShortMessageService.defaultProps = {
    crmId: null,
};

export default ShortMessageService;
